// src/components/ProjectModal.jsx
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProjectModal({ project, onClose }) {
  const modalRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // close on Esc
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Prevent body scroll when modal is open and compensate for scrollbar width
  useEffect(() => {
    if (project) {
      // Get scrollbar width before hiding it
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Store original values
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      const originalPaddingRight = window.getComputedStyle(document.body).paddingRight;

      // Lock scroll and add padding to prevent shift
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        // Restore original values
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [project]);

  // Check if modal content is scrollable and handle scroll indicator
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const checkScrollable = () => {
      const hasScroll = modal.scrollHeight > modal.clientHeight;
      setIsScrollable(hasScroll);
      setShowScrollIndicator(hasScroll && modal.scrollTop < 50);
    };

    checkScrollable();

    const handleScroll = () => {
      if (modal.scrollTop > 50) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(isScrollable);
      }
    };

    modal.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkScrollable);

    return () => {
      modal.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [project, isScrollable]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-slate-900/80 dark:bg-slate-950/90 backdrop-blur-sm" onClick={onClose} />

          {/* Modal */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-3xl border-4 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl
                       flex flex-col max-h-[90vh] transition-colors duration-300"
            role="dialog"
            aria-modal="true"
            aria-label={project.title}
          >
            {/* Header - Fixed */}
            <div className="border-b-2 border-slate-900 dark:border-slate-600 p-6 flex items-start justify-between gap-4 flex-shrink-0">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-1">
                  {project.title}
                </h3>
                {project.completion_date && (
                  <div className="text-sm font-mono text-slate-600 dark:text-slate-400">
                    {new Date(project.completion_date).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={onClose}
                className="px-3 py-1 bg-slate-900 dark:bg-slate-700 text-white text-sm font-mono hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
                aria-label="Close modal"
              >
                ESC
              </button>
            </div>

            {/* Scroll Indicator */}
            <AnimatePresence>
              {showScrollIndicator && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-[88px] left-1/2 -translate-x-1/2 z-10 pointer-events-none"
                >
                  <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-mono rounded-full shadow-lg"
                  >
                    ↓ Scroll for more
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content - Scrollable */}
            <div
              ref={modalRef}
              className="overflow-y-auto overflow-x-hidden flex-1 scroll-smooth modal-scroll no-bounce"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#475569 #e2e8f0'
              }}
            >
              <div className="p-6 space-y-6 pb-8">
              {/* Media Gallery */}
              {project.media && project.media.url && (
                <div className="border-2 border-slate-900 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 overflow-hidden transition-colors duration-300">
                  {project.media.type === 'video' ? (
                    <video
                      src={project.media.url}
                      controls
                      className="w-full h-auto"
                      poster={project.media.thumbnail}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      src={project.media.url}
                      alt={project.title}
                      className="w-full h-auto object-cover"
                    />
                  )}
                </div>
              )}

              {/* Impact Metrics */}
              {project.impact && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-700 dark:to-slate-600 border-2 border-slate-900 dark:border-slate-500 p-5 transition-colors duration-300">
                  <h4 className="text-xs font-mono font-bold text-slate-500 dark:text-slate-300 uppercase mb-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                    Impact & Results
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {project.impact.metric1 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 text-center hover:shadow-md transition-all"
                      >
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {project.impact.metric1}
                        </div>
                      </motion.div>
                    )}
                    {project.impact.metric2 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 text-center hover:shadow-md transition-all"
                      >
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {project.impact.metric2}
                        </div>
                      </motion.div>
                    )}
                    {project.impact.metric3 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 text-center hover:shadow-md transition-all"
                      >
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {project.impact.metric3}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}

              {/* Featured badge */}
              {project.featured && (
                <div className="inline-block px-3 py-2 bg-yellow-400 text-slate-900 text-sm font-mono font-black uppercase tracking-wider border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)]">
                  ★ FEATURED PROJECT
                </div>
              )}

              {/* Category Tags */}
              {project.tags && project.tags.length > 0 && (
                <div>
                  <h4 className="text-xs font-mono font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-bold px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 uppercase transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h4 className="text-xs font-mono font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">Description</h4>
                <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
                  {project.description}
                </p>
              </div>

              {/* Technologies */}
              {project.technologies && project.technologies.length > 0 && (
                <div>
                  <h4 className="text-xs font-mono font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">Technologies Used</h4>
                  <div className="text-sm font-mono text-slate-700 dark:text-slate-300">
                    {project.technologies.join(' · ')}
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="flex flex-wrap gap-3 pt-4 border-t-2 border-slate-900 dark:border-slate-600">
                {project.live_demo && (
                  <a
                    href={project.live_demo}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 bg-slate-900 dark:bg-slate-700 text-white text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
                  >
                    View Live Demo →
                  </a>
                )}
                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm font-medium hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white transition-colors"
                  >
                    View Source Code
                  </a>
                )}
                {!project.live_demo && !project.github && (
                  <p className="text-sm font-mono text-slate-500 dark:text-slate-400 italic">
                    No public links available
                  </p>
                )}
              </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
